<template>
    <div class="secure-payment-group" @keydown.esc="dismissHelp">
        <div class="col-12">
            <div class="secure-payment-panel">
                <div class="secure-payment-header">
                    <span class="secure-payment-heading">
                        <span class="secure-payment-icon">
                            <feather type="shield" size="18" />
                        </span>
                        <span>
                            <span class="secure-payment-title-line">
                                <strong>{{ $tl("labels.booking.services.footprintAndPayment.title") }}</strong>
                            </span>
                        </span>
                    </span>
                    <span class="secure-payment-header-actions">
                        <span class="secure-payment-status" :class="{ 'is-active': hasSecurePaymentEnabled }">
                            {{ paymentStatusLabel }}
                        </span>
                    </span>
                </div>

                <div class="secure-payment-panel-body">
                    <div v-if="!hasPaymentSystem" class="secure-payment-alert">
                        <feather type="alert-circle" size="18" />
                        <span>{{ $tl("infos.common.needStripeAccount") }}</span>
                    </div>

                    <div v-else class="menu-option">
                        <overlay-layout :display-overlay="!hasPaymentSystem">
                            <template v-slot:content>
                                <div v-if="!supportsCardStorage" class="secure-payment-alert" role="note">
                                    {{ $tl("labels.booking.services.payment.cardStorageUnavailable") }}
                                </div>
                                <div class="secure-payment-flow">
                                    <section
                                        class="secure-payment-card secure-payment-card--capture"
                                        :class="{ 'is-enabled': hasCardCaptureEnabled }">
                                        <div class="secure-payment-card-header">
                                            <div class="secure-payment-card-main">
                                                <div class="secure-payment-card-title-row">
                                                    <div class="secure-payment-card-title secure-payment-card-title--with-switch">
                                                        <span class="secure-payment-title-copy">
                                                            <span>
                                                                {{ $tl("labels.booking.services.payment.cardCapture") }}
                                                            </span>
                                                        </span>
                                                        <label class="form-check form-switch secure-payment-switch">
                                                            <input
                                                                class="form-check-input"
                                                                type="checkbox"
                                                                role="switch"
                                                                :checked="hasCardCaptureEnabled"
                                                                :aria-label="$tl('labels.booking.services.payment.enableCardCapture')"
                                                                @change="setCardCaptureEnabled($event.target.checked)" />
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <p class="secure-payment-description">{{ cardCaptureHelp }}</p>
                                        <p v-if="supportsCardStorage" class="secure-payment-description">
                                            {{ $tl("labels.booking.services.payment.cardCaptureNoShowDescription") }}
                                        </p>
                                        <div v-if="hasCardCaptureEnabled" class="secure-payment-card-body">
                                            <div class="secure-payment-fields">
                                                <div class="secure-payment-field secure-payment-field--threshold mb-0">
                                                    <label class="form-label" for="secure-payment-card-capture-pax">{{
                                                        $tl("labels.booking.services.payment.from")
                                                    }}</label>
                                                    <div class="secure-payment-inline-input">
                                                        <input
                                                            id="secure-payment-card-capture-pax"
                                                            type="number"
                                                            v-model="service.payment.bankImprint.cardCaptureEnabledAbovePax"
                                                            min="1"
                                                            step="1"
                                                            @blur="normalizeCardCapturePax"
                                                            class="form-control" />
                                                        <span class="secure-payment-unit">{{
                                                            $tl("labels.booking.services.payment.peopleUnit")
                                                        }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </section>

                                    <div v-if="hasCardCaptureEnabled" class="secure-payment-dependent-stack">
                                        <section class="secure-payment-card" :class="{ 'is-enabled': service.payment.bankImprint.securityEnabled }">
                                            <div class="secure-payment-card-title-row">
                                                <div class="secure-payment-card-title secure-payment-card-title--with-switch">
                                                    <span class="secure-payment-title-copy">
                                                        <span>
                                                            {{ $tl("labels.booking.services.payment.bankImprint") }}
                                                        </span>
                                                    </span>
                                                    <label class="form-check form-switch secure-payment-switch">
                                                        <input
                                                            class="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            :checked="service.payment.bankImprint.securityEnabled"
                                                            :disabled="!supportsCardStorage"
                                                            :aria-label="$tl('labels.booking.services.payment.enableBankImprint')"
                                                            @change="setBankImprintSecurityEnabled($event.target.checked)" />
                                                    </label>
                                                </div>
                                            </div>
                                            <p class="secure-payment-description">
                                                {{ $tl("labels.booking.services.payment.bankImprintDescription") }}
                                            </p>
                                            <p class="secure-payment-description">
                                                {{ $tl("labels.booking.services.payment.bankImprintExpiryDescription") }}
                                            </p>
                                            <p v-if="supportsCardStorage" class="secure-payment-description">
                                                {{ $tl("labels.booking.services.payment.bankImprintDeferredDescription") }}
                                                {{ bankImprintTiming }}
                                            </p>
                                            <p class="secure-payment-description">
                                                {{ $tl("labels.booking.services.payment.bankImprintAmountDescription") }}
                                            </p>
                                            <div v-if="service.payment.bankImprint.securityEnabled" class="secure-payment-failure-policy radio mt-3">
                                                <div class="secure-payment-field secure-payment-field--threshold mb-0 secure-payment-failure-row">
                                                    <label class="form-label" for="secure-payment-bank-imprint-pax">{{
                                                        $tl("labels.booking.services.payment.from")
                                                    }}</label>
                                                    <div class="secure-payment-inline-input">
                                                        <input
                                                            id="secure-payment-bank-imprint-pax"
                                                            type="number"
                                                            :disabled="!supportsCardStorage"
                                                            v-model="service.payment.bankImprint.enabledAbovePax"
                                                            :min="cardCaptureMinimumPax"
                                                            step="1"
                                                            @blur="normalizeBankImprintPax"
                                                            class="form-control" />
                                                        <span class="secure-payment-unit">{{
                                                            $tl("labels.booking.services.payment.peopleUnit")
                                                        }}</span>
                                                    </div>
                                                </div>
                                                <label class="secure-payment-failure-option">
                                                    <input class="form-check-input" type="checkbox" v-model="service.payment.autoCancel" />
                                                    <span>{{ $tl("labels.booking.services.payment.autoCancelOnBankImprintFailure") }}</span>
                                                </label>
                                            </div>
                                        </section>

                                        <section
                                            class="secure-payment-card"
                                            :class="{
                                                'is-enabled': service.payment.bankImprint.noShowGuaranteeEnabled,
                                                'is-disabled': !hasCardCaptureEnabled,
                                            }">
                                            <div class="secure-payment-card-header">
                                                <div class="secure-payment-card-main">
                                                    <div class="secure-payment-card-title-row">
                                                        <div class="secure-payment-card-title secure-payment-card-title--with-switch">
                                                            <span class="secure-payment-title-copy">
                                                                <span>
                                                                    {{ $tl("labels.booking.services.payment.noShowGuarantee") }}
                                                                </span>
                                                            </span>
                                                            <label class="form-check form-switch secure-payment-switch">
                                                                <input
                                                                    class="form-check-input"
                                                                    type="checkbox"
                                                                    role="switch"
                                                                    :checked="service.payment.bankImprint.noShowGuaranteeEnabled"
                                                                    :disabled="!hasCardCaptureEnabled"
                                                                    :aria-label="$tl('labels.booking.services.payment.enableNoShowGuarantee')"
                                                                    @change="setNoShowGuaranteeEnabled($event.target.checked)" />
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <p class="secure-payment-description">
                                                {{ noShowGuaranteeHelp }}
                                            </p>
                                            <div
                                                v-if="service.payment.bankImprint.noShowGuaranteeEnabled && hasCardCaptureEnabled"
                                                class="secure-payment-card-body">
                                                <div class="secure-payment-fields">
                                                    <div class="secure-payment-field mb-0 secure-payment-amount-field">
                                                        <label class="form-label" for="secure-payment-no-show-amount">{{
                                                            $tl("labels.booking.services.payment.noShowAmount")
                                                        }}</label>
                                                        <div class="secure-payment-inline-input">
                                                            <input
                                                                id="secure-payment-no-show-amount"
                                                                type="number"
                                                                v-model="service.payment.bankImprint.amount"
                                                                class="form-control"
                                                                min="0"
                                                                step="0.01" />
                                                            <span class="secure-payment-unit">€</span>
                                                        </div>
                                                    </div>
                                                    <div class="secure-payment-choice secure-payment-choice--amount-mode">
                                                        <span>{{ $tl("labels.booking.services.payment.noShowAmountMode") }}</span>
                                                        <div
                                                            class="secure-payment-segmented secure-payment-segmented--amount-mode"
                                                            role="radiogroup"
                                                            :aria-label="$tl('labels.booking.services.payment.noShowAmountMode')">
                                                            <label
                                                                class="form-label secure-payment-segmented-option"
                                                                :class="{
                                                                    'is-selected': service.payment.bankImprint.amountMode === 'per_cover',
                                                                }"
                                                                @click="service.payment.bankImprint.amountMode = 'per_cover'">
                                                                <input
                                                                    type="radio"
                                                                    name="noShowAmountMode"
                                                                    class="form-check-input"
                                                                    value="per_cover"
                                                                    v-model="service.payment.bankImprint.amountMode" />
                                                                <span>{{ $tl("labels.booking.services.payment.noShowAmountPerCover") }}</span>
                                                            </label>
                                                            <label
                                                                class="form-label secure-payment-segmented-option"
                                                                :class="{
                                                                    'is-selected': service.payment.bankImprint.amountMode === 'per_booking',
                                                                }"
                                                                @click="service.payment.bankImprint.amountMode = 'per_booking'">
                                                                <input
                                                                    type="radio"
                                                                    name="noShowAmountMode"
                                                                    class="form-check-input"
                                                                    value="per_booking"
                                                                    v-model="service.payment.bankImprint.amountMode" />
                                                                <span>{{ $tl("labels.booking.services.payment.noShowAmountPerBooking") }}</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>

                                        <section
                                            class="secure-payment-card"
                                            :class="{ 'is-enabled': service.payment.prepayment.enabled, 'is-disabled': !hasCardCaptureEnabled }">
                                            <div class="secure-payment-card-header">
                                                <div class="secure-payment-card-main">
                                                    <div class="secure-payment-card-title-row">
                                                        <div class="secure-payment-card-title secure-payment-card-title--with-switch">
                                                            <span class="secure-payment-title-copy">
                                                                <span>
                                                                    {{ $tl("labels.booking.services.payment.prepayment") }}
                                                                </span>
                                                            </span>
                                                            <label class="form-check form-switch secure-payment-switch">
                                                                <input
                                                                    class="form-check-input"
                                                                    type="checkbox"
                                                                    role="switch"
                                                                    name="prepayment_enabled"
                                                                    :checked="service.payment.prepayment.enabled"
                                                                    :disabled="!hasCardCaptureEnabled"
                                                                    :aria-label="$tl('labels.booking.services.payment.enablePrepayment')"
                                                                    @change="setPrepaymentEnabled($event.target.checked)" />
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <p class="secure-payment-description">
                                                {{ $tl("labels.booking.services.payment.prepaymentDescription") }}
                                            </p>
                                            <div v-if="service.payment.prepayment.enabled && hasCardCaptureEnabled" class="secure-payment-card-body">
                                                <div class="secure-payment-toggle-line">
                                                    <div>
                                                        <span>{{ $tl("labels.booking.services.payment.allowPayOnSite") }}</span>
                                                        <button
                                                            v-tooltip="$tl('labels.booking.services.payment.allowPayOnSiteDescription')"
                                                            type="button"
                                                            class="secure-payment-help"
                                                            :aria-label="helpLabel($tl('labels.booking.services.payment.allowPayOnSite'))"
                                                            @click="showHelp">
                                                            <feather type="info" size="14" aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                    <label class="form-check form-switch secure-payment-switch">
                                                        <input
                                                            class="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            v-model="service.payment.prepayment.allowPayOnSite"
                                                            :aria-label="$tl('labels.booking.services.payment.allowPayOnSite')" />
                                                    </label>
                                                </div>

                                                <div class="secure-payment-choice secure-payment-choice--prepayment-mode">
                                                    <span>{{ $tl("labels.booking.services.payment.prepaymentCalculationMode") }}</span>
                                                    <div
                                                        class="secure-payment-segmented secure-payment-segmented--prepayment-mode"
                                                        role="radiogroup"
                                                        :aria-label="$tl('labels.booking.services.payment.prepaymentCalculationMode')">
                                                        <label
                                                            class="form-label"
                                                            :class="{
                                                                'is-selected': service.payment.prepayment.calculationMode === 'cart_percentage',
                                                            }">
                                                            <input
                                                                type="radio"
                                                                name="prepaymentCalculationMode"
                                                                class="form-check-input"
                                                                v-model="service.payment.prepayment.calculationMode"
                                                                value="cart_percentage" />
                                                            <span>{{ $tl("labels.booking.services.payment.prepaymentCartPercentage") }}</span>
                                                        </label>
                                                        <label
                                                            class="form-label"
                                                            :class="{
                                                                'is-selected': service.payment.prepayment.calculationMode === 'fixed_per_cover',
                                                            }">
                                                            <input
                                                                type="radio"
                                                                name="prepaymentCalculationMode"
                                                                class="form-check-input"
                                                                v-model="service.payment.prepayment.calculationMode"
                                                                value="fixed_per_cover" />
                                                            <span>{{ $tl("labels.booking.services.payment.prepaymentFixedPerCover") }}</span>
                                                        </label>
                                                        <label
                                                            class="form-label"
                                                            :class="{
                                                                'is-selected': service.payment.prepayment.calculationMode === 'fixed_per_booking',
                                                            }">
                                                            <input
                                                                type="radio"
                                                                name="prepaymentCalculationMode"
                                                                class="form-check-input"
                                                                v-model="service.payment.prepayment.calculationMode"
                                                                value="fixed_per_booking" />
                                                            <span>{{ $tl("labels.booking.services.payment.prepaymentFixedPerBooking") }}</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div class="secure-payment-fields">
                                                    <div
                                                        v-if="service.payment.prepayment.calculationMode === 'cart_percentage'"
                                                        class="secure-payment-field mb-0">
                                                        <label class="form-label" for="secure-payment-prepayment-percent">{{
                                                            $tl("labels.booking.services.payment.prepayment")
                                                        }}</label>
                                                        <div class="secure-payment-inline-input">
                                                            <input
                                                                id="secure-payment-prepayment-percent"
                                                                type="number"
                                                                name="prepayment_percent"
                                                                v-model="service.payment.prepayment.percentage"
                                                                class="form-control"
                                                                min="1"
                                                                max="100"
                                                                step="1" />
                                                            <span class="secure-payment-unit">%</span>
                                                        </div>
                                                    </div>
                                                    <div
                                                        v-if="service.payment.prepayment.calculationMode !== 'cart_percentage'"
                                                        class="secure-payment-field mb-0">
                                                        <label class="form-label" for="secure-payment-prepayment-fixed-amount">{{
                                                            $tl("labels.booking.services.payment.prepaymentFixedAmount")
                                                        }}</label>
                                                        <div class="secure-payment-inline-input">
                                                            <input
                                                                id="secure-payment-prepayment-fixed-amount"
                                                                type="number"
                                                                v-model="service.payment.prepayment.fixedAmount"
                                                                class="form-control"
                                                                min="1"
                                                                step="1" />
                                                            <span class="secure-payment-unit">€</span>
                                                        </div>
                                                    </div>
                                                    <div class="secure-payment-field secure-payment-field--threshold mb-0">
                                                        <label class="form-label" for="secure-payment-prepayment-pax">{{
                                                            $tl("labels.booking.services.payment.from")
                                                        }}</label>
                                                        <div class="secure-payment-inline-input">
                                                            <input
                                                                id="secure-payment-prepayment-pax"
                                                                type="number"
                                                                v-model="service.payment.prepayment.enabledAbovePax"
                                                                :min="cardCaptureMinimumPax"
                                                                step="1"
                                                                @blur="normalizePrepaymentPax"
                                                                class="form-control" />
                                                            <span class="secure-payment-unit">{{
                                                                $tl("labels.booking.services.payment.peopleUnit")
                                                            }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>
                                </div>

                                <send-reminder-payment
                                    v-if="showReminderSettings"
                                    v-model="service.sendReminderPayment"
                                    :cancel-auto="service.payment.autoCancel" />
                            </template>
                            <template v-slot:overlay>
                                <div class="is-white text-center d-table">
                                    <h6 class="d-table-cell align-middle">{{ $tl("infos.common.needStripeAccount") }}</h6>
                                </div>
                            </template>
                        </overlay-layout>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Tooltip } from "bootstrap";
import OverlayLayout from "../../layouts/OverlayLayout.vue";
import SendReminderPayment from "../../../views/Booking/Settings/Services/Forms/SendReminderPayment.vue";

export default {
    name: "PaymentGroup",
    props: {
        service: {
            type: Object,
            required: true,
        },
        showReminderSettings: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        restaurant_id() {
            return this.$route.params.restaurant_id;
        },
        restaurant() {
            return this.$store.getters["restaurants/findRestaurantById"](this.restaurant_id);
        },
        hasPaymentSystem() {
            return this.restaurant && this.restaurant.has_psp;
        },
        supportsCardStorage() {
            const provider = this.restaurant?.card_payment_system_type || this.restaurant?.prefered_payment_system;
            return provider === "stripe" || (!provider && Boolean(this.restaurant?.stripe_client_id));
        },
        optionBankShortDelay() {
            return this.$store.getters["widgets/getWidget"].option_bank_short_delay || 1;
        },
        hasBillableItems() {
            return (
                (this.service.menus.value && !this.$_.isEmpty(this.service.menus.value)) ||
                (this.service.generalOptions.value && !this.$_.isEmpty(this.service.generalOptions.value))
            );
        },
        hasCardCaptureEnabled() {
            return this.service.payment.bankImprint.enabled;
        },
        cardCaptureHelp() {
            return this.$tl(
                this.supportsCardStorage
                    ? "labels.booking.services.payment.cardCaptureDescription"
                    : "labels.booking.services.payment.cardStorageUnavailable"
            );
        },
        hasSecurePaymentEnabled() {
            return this.hasCardCaptureEnabled || this.service.payment.prepayment.enabled;
        },
        cardCaptureMinimumPax() {
            return Math.max(1, Number(this.service.payment.bankImprint.cardCaptureEnabledAbovePax || 1));
        },
        paymentStatusLabel() {
            return this.hasSecurePaymentEnabled
                ? this.$tl("labels.booking.services.payment.active")
                : this.$tl("labels.booking.services.payment.inactive");
        },
        cardCaptureOverview() {
            if (!this.service.payment.bankImprint.enabled) {
                return this.$tl("labels.booking.services.payment.cardCaptureInactive");
            }

            const minPax = this.service.payment.bankImprint.cardCaptureEnabledAbovePax || 1;

            return `${this.$tl("labels.booking.services.payment.from")} ${minPax} ${
                Number(minPax) > 1 ? this.$tl("labels.booking.services.capacity.peopleLower") : this.$tl("labels.booking.services.capacity.person")
            }`;
        },
        bankImprintOverview() {
            if (!this.hasCardCaptureEnabled) {
                return this.$tl("labels.booking.services.payment.cardRequired");
            }
            if (!this.service.payment.bankImprint.securityEnabled) {
                return this.$tl("labels.booking.services.payment.optionAvailableInactive");
            }

            return `${this.$tl("labels.booking.services.payment.active")} · ${this.bankImprintLeadLabel}`;
        },
        bankImprintLeadLabel() {
            return `J-${Math.min(this.service.payment.bankImprint.captureLeadDays || 3, 3)}`;
        },
        bankImprintTiming() {
            return this.$tl("labels.booking.services.payment.bankImprintTiming", this.restaurant_id, { delay: this.bankImprintLeadLabel });
        },
        bankImprintSchedule() {
            return this.$tl("labels.booking.services.payment.bankImprintSchedule", this.restaurant_id, { delay: this.bankImprintLeadLabel });
        },
        noShowGuaranteeHelp() {
            const description = this.$tl("labels.booking.services.payment.noShowGuaranteeDescription");
            return this.service.payment.bankImprint.securityEnabled
                ? `${description} ${this.$tl("labels.booking.services.payment.guaranteeRequiredByImprint")}`
                : description;
        },
        prepaymentOverview() {
            if (!this.hasBillableItems) {
                return this.$tl("labels.booking.services.payment.billableItemsRequired");
            }

            if (!this.hasCardCaptureEnabled) {
                return this.$tl("labels.booking.services.payment.cardRequired");
            }

            if (!this.service.payment.prepayment.enabled) {
                return this.$tl("labels.booking.services.payment.optionAvailableInactive");
            }

            const percent = this.service.payment.prepayment.percentage || 100;
            const type = this.service.payment.prepayment.allowPayOnSite
                ? this.$tl("labels.booking.services.payment.payOnSiteAllowedShort")
                : this.$tl("labels.booking.services.payment.onlinePaymentRequiredShort");

            return `${percent}% · ${type}`;
        },
    },
    methods: {
        showHelp(event) {
            event.currentTarget.focus();
            Tooltip.getInstance(event.currentTarget)?.show();
        },
        dismissHelp(event) {
            const button = event.target.closest(".secure-payment-help");
            if (button) {
                Tooltip.getInstance(button)?.hide();
            }
        },
        helpLabel(label) {
            return this.$tl("labels.booking.services.payment.helpAbout", this.restaurant_id, { label });
        },
        setCardCaptureEnabled(enabled) {
            this.service.payment.bankImprint.enabled = enabled;
            if (enabled) {
                if (!this.supportsCardStorage) {
                    this.setNoShowGuaranteeEnabled(true);
                }
                this.normalizeCardCapturePax();
            }
            if (!enabled) {
                this.service.payment.bankImprint.noShowGuaranteeEnabled = false;
                this.service.payment.bankImprint.securityEnabled = false;
                this.service.payment.prepayment.enabled = false;
                this.service.payment.autoCancel = false;
            }
        },
        setNoShowGuaranteeEnabled(enabled) {
            if (!this.hasCardCaptureEnabled) {
                this.service.payment.bankImprint.noShowGuaranteeEnabled = false;
                return;
            }

            this.service.payment.bankImprint.noShowGuaranteeEnabled = enabled;
            if (enabled && !this.supportsCardStorage) {
                this.service.payment.bankImprint.securityEnabled = true;
            }
            if (!enabled) {
                this.service.payment.bankImprint.securityEnabled = false;
                this.service.payment.autoCancel = false;
            }
            if (enabled && !Number(this.service.payment.bankImprint.amount || 0)) {
                this.service.payment.bankImprint.amount = 20;
            }
            if (enabled) {
                this.normalizeCardCapturePax();
            }
        },
        setBankImprintSecurityEnabled(enabled) {
            if (!enabled && !this.supportsCardStorage && this.service.payment.bankImprint.noShowGuaranteeEnabled) {
                return;
            }
            if (!this.hasCardCaptureEnabled) {
                this.service.payment.bankImprint.securityEnabled = false;
                return;
            }

            this.service.payment.bankImprint.securityEnabled = enabled;
            if (enabled) {
                this.setNoShowGuaranteeEnabled(true);
            }
            if (enabled && this.service.payment.autoCancel === null) {
                this.service.payment.autoCancel = false;
            }
            if (enabled) {
                this.normalizeCardCapturePax();
            }
        },
        setPrepaymentEnabled(enabled) {
            if (!this.hasCardCaptureEnabled) {
                this.service.payment.prepayment.enabled = false;
                return;
            }

            this.service.payment.prepayment.enabled = enabled;
            if (enabled && this.service.payment.prepayment.allowPayOnSite === null) {
                this.service.payment.prepayment.allowPayOnSite = true;
            }
            if (enabled) {
                this.normalizePrepaymentPax();
            }
        },
        normalizeCardCapturePax() {
            const minPax = this.normalizePax(this.service.payment.bankImprint.cardCaptureEnabledAbovePax, 1);
            this.service.payment.bankImprint.cardCaptureEnabledAbovePax = minPax;

            this.syncDependentPaxWithCardCapture(minPax);
        },
        normalizeBankImprintPax() {
            if (!this.supportsCardStorage) {
                this.service.payment.bankImprint.enabledAbovePax = this.cardCaptureMinimumPax;
                return;
            }
            this.service.payment.bankImprint.enabledAbovePax = this.normalizePax(
                this.service.payment.bankImprint.enabledAbovePax,
                this.cardCaptureMinimumPax
            );
        },
        syncDependentPaxWithCardCapture(minPax) {
            if (this.service.payment.bankImprint.securityEnabled) {
                this.service.payment.bankImprint.enabledAbovePax = this.supportsCardStorage
                    ? this.normalizePax(this.service.payment.bankImprint.enabledAbovePax, minPax)
                    : minPax;
            }

            if (this.service.payment.prepayment.enabled) {
                this.service.payment.prepayment.enabledAbovePax = this.normalizePax(this.service.payment.prepayment.enabledAbovePax, minPax);
            }
        },
        normalizePrepaymentPax() {
            this.service.payment.prepayment.enabledAbovePax = this.normalizePax(
                this.service.payment.prepayment.enabledAbovePax,
                this.cardCaptureMinimumPax
            );
        },
        normalizePax(value, minimum) {
            const parsed = Number.parseInt(value, 10);
            if (Number.isNaN(parsed)) {
                return minimum;
            }

            return Math.max(minimum, parsed);
        },
        displayError(error) {
            this.$emit("has-errors", error);
        },
    },
    watch: {
        "service.payment.bankImprint.cardCaptureEnabledAbovePax"(value) {
            const parsed = Number.parseInt(value, 10);
            if (!Number.isNaN(parsed) && parsed >= 1) {
                this.syncDependentPaxWithCardCapture(parsed);
            }
        },
    },
    components: {
        OverlayLayout,
        SendReminderPayment,
    },
};
</script>

<style scoped>
.secure-payment-group {
    --guarantee-text: #222222;
    --guarantee-blue: #247892;
    --guarantee-accent: var(--main-color-light, #30a4b7);
    --guarantee-border: #e3e9ed;
    color: var(--guarantee-text);
}
.secure-payment-panel {
    background: #fff;
    border: 1px solid var(--guarantee-border);
    border-radius: 12px;
    padding: 22px;
}
.secure-payment-header,
.secure-payment-heading,
.secure-payment-card-title--with-switch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}
.secure-payment-heading {
    justify-content: flex-start;
    gap: 10px;
}
.secure-payment-header {
    margin-bottom: 16px;
}
.secure-payment-icon {
    display: inline-flex;
    color: var(--guarantee-blue);
}
.secure-payment-heading strong {
    font-size: 17px;
    color: var(--guarantee-text);
}
.secure-payment-status {
    font-size: 11px;
    color: #667782;
    white-space: nowrap;
}
.secure-payment-status.is-active {
    color: var(--guarantee-text);
}
.secure-payment-help {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--guarantee-blue);
    vertical-align: middle;
}
.secure-payment-help:hover,
.secure-payment-help:focus-visible {
    color: var(--guarantee-blue);
}
.secure-payment-help:focus-visible {
    outline: 2px solid var(--guarantee-blue);
    outline-offset: 2px;
}
.secure-payment-alert {
    padding: 12px;
    margin: 16px 0;
    border: 1px solid var(--guarantee-border);
    border-radius: 8px;
    font-size: 13px;
    display: flex;
    gap: 8px;
}
.secure-payment-flow,
.secure-payment-dependent-stack {
    display: grid;
    gap: 16px;
}
.secure-payment-card {
    padding: 18px;
    border: 1px solid var(--guarantee-border);
    border-radius: 10px;
    min-width: 0;
    background: #fff;
}
.secure-payment-card-main,
.secure-payment-title-copy {
    min-width: 0;
    flex: 1;
}
.secure-payment-title-copy > span {
    display: block;
    color: var(--guarantee-text);
    font-size: 15px;
    font-weight: 600;
    line-height: 1.4;
}
.secure-payment-title-copy > small {
    display: block;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.5;
    margin-top: 4px;
    color: #657580;
}
.secure-payment-card-body {
    margin-top: 12px;
}
.secure-payment-description {
    margin: 12px 0 0;
    color: #657580;
    font-size: 13px;
    line-height: 1.6;
}
.secure-payment-switch {
    flex: 0 0 48px;
    width: 48px;
    min-height: 40px;
    margin: 0;
    display: flex;
    align-items: center;
    padding-left: 0;
}
.secure-payment-switch .form-check-input {
    float: none;
    margin: 0;
    width: 38px;
    height: 22px;
    cursor: pointer;
}
.secure-payment-switch .form-check-input:disabled {
    cursor: not-allowed;
}
.secure-payment-switch .form-check-input:checked {
    background-color: var(--guarantee-accent);
    border-color: var(--guarantee-accent);
}
.secure-payment-fields {
    display: flex;
    flex-wrap: wrap;
    align-items: end;
    gap: 18px 24px;
}
.secure-payment-field > label,
.secure-payment-choice > span {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: var(--guarantee-text);
    margin-bottom: 6px;
}
.secure-payment-inline-input {
    display: flex;
    align-items: center;
    gap: 8px;
}
.secure-payment-field--threshold {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}
.secure-payment-field--threshold > label {
    margin: 0;
}
.secure-payment-inline-input .form-control {
    width: 94px;
    min-height: 38px;
    font-size: 14px;
    color: var(--guarantee-text);
    border-radius: 7px !important;
}
.secure-payment-inline-input .form-control:focus-visible {
    outline: 2px solid var(--guarantee-blue);
    outline-offset: 2px;
}
.secure-payment-field--threshold .form-control {
    width: 76px;
}
.secure-payment-unit {
    font-size: 12px;
    color: #657580;
}
.secure-payment-failure-policy {
    display: grid;
    gap: 16px;
}
.secure-payment-toggle-line {
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
}
.secure-payment-failure-option {
    display: flex;
    align-items: center;
    justify-self: start;
    gap: 10px;
    min-height: 44px;
    margin: 0;
    padding: 8px 10px;
    border: 1px solid var(--guarantee-border);
    border-radius: 7px;
    background: #fff;
    color: var(--guarantee-text);
    font-size: 13px;
    line-height: 1.5;
    cursor: pointer;
}
.secure-payment-failure-option:hover,
.secure-payment-failure-option:has(input:checked) {
    border-color: var(--guarantee-accent);
    background: rgba(48, 164, 183, 0.04);
}
.secure-payment-failure-option .form-check-input {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    margin: 0;
    border-color: var(--guarantee-blue);
    cursor: pointer;
}
.secure-payment-failure-option .form-check-input:checked {
    background-color: var(--guarantee-accent);
    border-color: var(--guarantee-accent);
}
.secure-payment-failure-option .form-check-input:focus {
    box-shadow: none;
}
.secure-payment-failure-option:has(input:focus-visible) {
    outline: 2px solid var(--guarantee-blue);
    outline-offset: 2px;
}
.secure-payment-toggle-line {
    margin-bottom: 18px;
    font-size: 13px;
    color: var(--guarantee-text);
}
.secure-payment-choice--prepayment-mode {
    margin-bottom: 18px;
}
.secure-payment-segmented {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.secure-payment-segmented label {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
    padding: 8px 12px;
    margin: 0;
    border: 1px solid var(--guarantee-border);
    border-radius: 7px;
    color: var(--guarantee-text);
    cursor: pointer;
    font-size: 12px;
}
.secure-payment-segmented label.is-selected {
    border-color: var(--guarantee-accent);
}
.secure-payment-segmented input {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
}
.secure-payment-segmented input:checked {
    background-color: var(--guarantee-accent);
    border-color: var(--guarantee-accent);
}
.secure-payment-segmented input:focus {
    border-color: var(--guarantee-accent);
    box-shadow: none;
}
.secure-payment-segmented label:has(input:focus-visible) {
    outline: 2px solid var(--guarantee-blue);
    outline-offset: 2px;
}
@media (max-width: 575px) {
    .secure-payment-panel {
        padding: 16px;
    }
    .secure-payment-card {
        padding: 14px;
    }
    .secure-payment-title-copy > span {
        font-size: 14px;
    }
    .secure-payment-fields {
        gap: 16px;
    }
    .secure-payment-segmented {
        width: 100%;
    }
    .secure-payment-segmented label {
        flex: 1 1 120px;
    }
}
</style>
